import {
  eyeGlassesIcon,
  terminalHeaderByLangIcon,
  terminalHeaderCalendarIcon,
  terminalHeaderClockIcon,
  terminalHeaderEnLangIcon,
  terminalHeaderRuLangIcon,
  terminalHeaderSelectorArrowDownIcon,
  terminalHeaderSelectorArrowUpIcon
} from '../../../../../assets/icons';
import classes from './TerminalHeader.module.scss';
import React, { FC, useEffect, useRef, useState } from 'react';
import { languageType } from '../NormalMode/NormalMode';
import { format } from 'date-fns';
import { be, ru } from 'date-fns/locale';
import { TerminalMode } from '../../../../../pagesMain/TerminalPage/TerminalPage';
import classNames from 'classnames';
import { TerminalAppearanceResponse } from '../../../../../types/Admin/TerminalAppearanceTypes';
import { baseSoundURL } from '../../../../../config';
import tehnoSkyTerminalLogo from '../../../../../assets/icons/tehnoSkyTerminalLogo.svg';

type NormalHeaderType = {
  setMode: (mode: TerminalMode) => void;
  language: languageType;
  setLanguage: (newLanguage: languageType) => void;
  appearance?: TerminalAppearanceResponse;
};

const localeArray = {
  en: undefined,
  by: be,
  ru: ru
};

const languagesList = [
  {
    iconSmall: <img src={terminalHeaderRuLangIcon} width="20px" alt="RuLangIcon" />,
    iconFull: <img src={terminalHeaderRuLangIcon} width="30px" alt="RuLangIcon" />,
    shortTitle: 'ru' as languageType,
    fullTitle: 'Русский язык'
  },
  {
    iconSmall: <img src={terminalHeaderByLangIcon} width="20px" alt="ByLangIcon" />,
    iconFull: <img src={terminalHeaderByLangIcon} width="30px" alt="ByLangIcon" />,
    shortTitle: 'by' as languageType,
    fullTitle: 'Белорусский язык'
  },
  {
    iconSmall: <img src={terminalHeaderEnLangIcon} width="20px" alt="EngLangIcon" />,
    iconFull: <img src={terminalHeaderEnLangIcon} width="30px" alt="EngLangIcon" />,
    shortTitle: 'en' as languageType,
    fullTitle: 'English'
  }
];

const normalizeMediaUrl = (url?: string | null) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const getLogoBackgroundSize = (fitMode: string | null | undefined, scale: number) => {
  switch (fitMode) {
    case 'contain':
      return 'contain';
    case 'cover':
      return 'cover';
    case 'stretch':
      return '100% 100%';
    case 'width':
      return '100% auto';
    case 'height':
      return 'auto 100%';
    case 'none':
      return 'auto';
    case 'scale':
    default:
      return `${scale}% ${scale}%`;
  }
};

const NormalHeader: FC<NormalHeaderType> = ({
  setMode,
  language,
  setLanguage,
  appearance,
}) => {
  const [time, setTime] = useState<string>();
  const [date, setDate] = useState('');
  const [selectedLang, setSelectedLang] = useState(languagesList[0].fullTitle);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const toggleLangDropdownList = () => {
    setIsLangDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const nextTime = format(now, 'HH:mm');
      const nextDate = format(now, 'd MMMM yyyy', { locale: localeArray[language] });

      setTime((prev) => (prev === nextTime ? prev : nextTime));
      setDate((prev) => (prev === nextDate ? prev : nextDate));
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, [language]);

  const handleChangeLanguage = (language: languageType): void => {
    setLanguage(language);
    setDate(format(new Date(), 'd MMMM yyyy', { locale: localeArray[language] }));
  };

  const handleClickMode = () => {
    setMode(TerminalMode.NotNormal);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsLangDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const logoSettings = appearance?.settings?.logo;
  const logoUrl = normalizeMediaUrl(logoSettings?.url) || tehnoSkyTerminalLogo;
  const logoScale = logoSettings?.scale ?? 100;
  const logoPositionX = logoSettings?.positionX ?? 50;
  const logoPositionY = logoSettings?.positionY ?? 50;
  const logoFitMode = logoSettings?.fitMode ?? 'scale';

  return (
    <header className={classes.normalHeaderContainer}>
      <div className={classes.logoDateContainer}>
        <div
          className={classes.logoImage}
          style={{
            backgroundImage: `url(${logoUrl})`,
            backgroundSize: getLogoBackgroundSize(logoFitMode, logoScale),
            backgroundPosition: `${logoPositionX}% ${logoPositionY}%`
          }}
          aria-label="Логотип терминала"
        />
      </div>

      <div className={classes.infoContainer}>
        <div className={classes.settingsWrapper}>
          <button className={classes.poorVisionIcon} onClick={() => handleClickMode()}>
            <img src={eyeGlassesIcon} alt="poorVisionIcon" />
          </button>

          <div
            className={classNames(classes.languagesContainer, {
              [classes.languagesContainerActive]: isLangDropdownOpen
            })}>
            <button onClick={toggleLangDropdownList}>
              {languagesList.find((e) => e.fullTitle === selectedLang).iconFull ||
                languagesList[0].iconFull}
              <span>{selectedLang}</span>
              <div>
                {isLangDropdownOpen && (
                  <img src={terminalHeaderSelectorArrowUpIcon} alt="SelectorArrowUpIcon" />
                )}
                {!isLangDropdownOpen && (
                  <img src={terminalHeaderSelectorArrowDownIcon} alt="SelectorArrowDownIcon" />
                )}
              </div>
            </button>

            {isLangDropdownOpen && (
              <div ref={modalRef} className={classes.languagesDropdownList}>
                {languagesList.map((e, idx) => {
                  if (selectedLang !== e.fullTitle) {
                    return (
                      <button
                        className={classes.langDropdownBtn}
                        onClick={() => {
                          setSelectedLang(e.fullTitle);
                          setIsLangDropdownOpen(false);
                          handleChangeLanguage(e.shortTitle);
                        }}
                        key={idx}>
                        {e.iconSmall}
                        <span>{e.shortTitle}</span>
                      </button>
                    );
                  }
                })}
              </div>
            )}
          </div>
        </div>

        <div className={classes.dateWrapper}>
          <div className={classes.date}>
            <img src={terminalHeaderCalendarIcon} alt="CalendarIcon" />
            <span>{date}</span>
          </div>

          <div className={classes.time}>
            <img src={terminalHeaderClockIcon} alt="CalendarIcon" />
            <span>{time}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NormalHeader;
